import React, { SetStateAction, useEffect, useState } from "react";
import {
    Button,
    ButtonToolbar,
    Card,
    Col,
    FloatingLabel,
    Form,
    Image,
    Row,
    Spinner,
    Toast,
    ToastContainer,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CurrencyInput from "../../../../../../../components/common/CurrencyInput";
import { useAuth } from "../../../../../../../providers";
import { IProduct } from "../../../../../business";
import { ProductControllerImpl } from "../../../../../business/impl";

export const ProductUpdateContent: React.FC<{ productData: IProduct }> = ({ productData }) => {
    const { setCallbackLogin, handleLogout } = useAuth();
    const { t } = useTranslation('product');
    const navigate = useNavigate();
    const controller = new ProductControllerImpl();
    const formatCurrency = new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
    });
    const removeCurrencySign = (number) => {
        const formatedValue = formatCurrency.format(number);
        return formatedValue.replace("R$", "").trim();
    };
    const [selectedImages, setSelectedImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState<Array<string>>(productData.image);
    const [price, setPrice] = useState(removeCurrencySign(productData.price));
    const [toasting, setToasting] = useState(null);
    const [isProccessing, setProccessing] = useState(false);

    const [formData, setFormData] = useState({
        partName: productData.part_name,
        manufacturer: productData.manufacturer,
        brand: productData.brand,
        model: productData.model,
        isNew: productData.is_new,
        isNational: productData.is_national,
        description: productData.description,
        quantity: productData.quantity,
        code: productData.product_code
    });

    const {
        partName,
        manufacturer,
        brand,
        model,
        isNew,
        isNational,
        description,
        quantity,
        code,
    } = formData;

    const handleFileChange = (e) => {
        const selectedFiles: SetStateAction<never[]> = Array.from(e.target.files);
        setSelectedImages(selectedFiles);

        const previews: SetStateAction<string[]> = selectedFiles.map((file) => URL.createObjectURL(file));
        setImagePreviews(previews);
    };

    useEffect(() => {
        return () => {
            imagePreviews.forEach((preview) => URL.revokeObjectURL(preview));
        };
    }, [imagePreviews]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setProccessing(true);
        const newNumber = e.target["price"].value
            .replace("R$ ", "")
            .replaceAll(".", "")
            .replace(",", "");
        const priceFloat = parseFloat(newNumber / 100);

        const submitedFormData = new FormData();
        submitedFormData.append("id", productData.id);
        submitedFormData.append("part_name", partName);
        submitedFormData.append("manufacturer", manufacturer);
        submitedFormData.append("brand", brand);
        submitedFormData.append("model", model);
        submitedFormData.append("is_new", (isNew ? 1 : 0).toString());
        submitedFormData.append("is_national", (isNational ? 1 : 0).toString());
        submitedFormData.append("description", description);
        submitedFormData.append("price", priceFloat.toString());
        submitedFormData.append("quantity", quantity.toString());
        submitedFormData.append("code", code);

        selectedImages.forEach((file, index) => {
            submitedFormData.append(`image${index}`, file);
        });
        try {
            const response = await controller.onUpdateProductSubmit(submitedFormData);
            if (response.success === true) {
                navigate("/adm/productList", {
                    state: {
                        showToast: true,
                        text: t('update.updated'),
                        image: response.coverImage,
                        date: new Date().toISOString(),
                    },
                });
            } else {
                setProccessing(false);
                if (response.description) {
                    let errorDescriptions = "";
                    Object.keys(response.description).forEach((key) => {
                        if (Array.isArray(response.description[key])) {
                            response.description[key].forEach((errorMessage) => {
                                errorDescriptions += `${errorMessage} `;
                            });
                        }
                    });
                    if (errorDescriptions === "") {
                        errorDescriptions = response.description;
                    }
                    setToasting(errorDescriptions);
                    setTimeout(() => { navigate("/adm/productList") }, 5000)
                } else {
                    setToasting(response);
                }
            }
        } catch (error) {
            console.error("Erro na requisição:", error);
            if (error === "401") {
                setCallbackLogin(
                    {
                        goBack: true,
                        origin: '/adm/dashboard',
                    }
                );
                handleLogout();
                navigate('/auth');
            } else {
                setProccessing(true);
            }
        }
    };

    return (
        <>
            <Card>
                <Card.Body>
                    <p className="text-muted mb-3">{t('update.title')}</p>
                    <Form onSubmit={handleFormSubmit}>
                        <fieldset disabled={isProccessing}>
                            <FloatingLabel
                                className="mb-3"
                                controlId="floatingInputGrid"
                                label={t('form.part_name.label')}
                            >
                                <Form.Control
                                    type="text"
                                    placeholder={t('form.part_name.placeholder')}
                                    className="text-capitalize"
                                    required
                                    autoComplete="off"
                                    aria-describedby="partNameHelpBlock"
                                    value={partName}
                                    disabled
                                />
                            </FloatingLabel>
                            <Form.Text className="d-none" id="partNameHelpBlock">
                                {t('accessibility.partName')}
                            </Form.Text>

                            <Form.Group className="mb-3" controlId="code">
                                <FloatingLabel
                                    className="mb-3"
                                    controlId="floatingInputGrid"
                                    label={t('form.code.label')}
                                >
                                    <Form.Control
                                        type="text"
                                        placeholder={t('form.code.placeholder')}
                                        autoComplete="off"
                                        aria-describedby="codeHelpBlock"
                                        value={code}
                                        onChange={(e) => {
                                            setFormData((prevData) => ({
                                                ...prevData,
                                                code: e.target.value,
                                            }));
                                        }}
                                    />
                                </FloatingLabel>
                            </Form.Group>
                            <Form.Text className="d-none" id="codeHelpBlock">
                                {t('accessibility.code')}
                            </Form.Text>

                            <Form.Group className="mb-3" controlId="manufacturer">
                                <FloatingLabel
                                    className="mb-3"
                                    controlId="floatingInputGrid"
                                    label={t('form.manufacturer.label')}
                                >
                                    <Form.Control
                                        type="text"
                                        placeholder={t('form.manufacturer.placeholder')}
                                        className="text-capitalize"
                                        required
                                        autoComplete="off"
                                        aria-describedby="manufacturerHelpBlock"
                                        value={manufacturer}
                                        onChange={(e) => {
                                            setFormData((prevData) => ({
                                                ...prevData,
                                                manufacturer: e.target.value,
                                            }));
                                        }}
                                    />
                                </FloatingLabel>
                            </Form.Group>
                            <Form.Text className="d-none" id="manufacturerHelpBlock">
                                {t('accessibility.manufacturer')}
                            </Form.Text>

                            <CurrencyInput
                                showPrefix
                                placeholder={t('form.price.placeholder')}
                                onChange={setPrice}
                                value={price}
                            >
                                {(inputProps) => (
                                    <FloatingLabel
                                        className="mb-3"
                                        controlId="floatingInputGrid"
                                        label={t('form.price.label')}
                                    >
                                        <Form.Control
                                            {...inputProps}
                                            type="tel"
                                            className=""
                                            autoComplete="off"
                                            aria-describedby="priceHelpBlock"
                                            required
                                            id="price"
                                        />
                                    </FloatingLabel>
                                )}
                            </CurrencyInput>
                            <Form.Text className="d-none" id="priceHelpBlock">
                                {t('accessibility.price')}
                            </Form.Text>

                            <FloatingLabel
                                className="mb-3"
                                controlId="floatingInputGrid"
                                label={t('form.quantity.label')}
                            >
                                <Form.Control
                                    controlId="quantity"
                                    type="number"
                                    placeholder={t('form.quantity.placeholder')}
                                    min={1}
                                    className="mt-3"
                                    required
                                    aria-describedby="quantityHelpBlock"
                                    onChange={(e) => {
                                        setFormData((prevData) => ({
                                            ...prevData,
                                            quantity: e.target.value,
                                        }));
                                    }}
                                    value={formData.quantity}
                                    style={{ width: 100 }}
                                />
                            </FloatingLabel>
                            <Form.Text className="d-none" id="quantityHelpBlock">
                                {t('accessibility.quantity')}
                            </Form.Text>

                            <Form.Group className="mt-3" controlId="description">
                                <FloatingLabel
                                    className="mb-3"
                                    controlId="floatingInputGrid"
                                    label={t('form.description.placeholder')}
                                >
                                    <Form.Control
                                        as="textarea"
                                        style={{ height: '150px' }}
                                        placeholder={t('form.description.placeholder')}
                                        aria-describedby="descriptionHelpBlock"
                                        required
                                        onChange={(e) => {
                                            setFormData((prevData) => ({
                                                ...prevData,
                                                description: e.target.value,
                                            }));
                                        }}
                                        value={formData.description}
                                    />
                                </FloatingLabel>
                            </Form.Group>
                            <Form.Text className="d-none" id="descriptionHelpBlock">
                                {t('accessibility.description')}
                            </Form.Text>

                            <p className="text-muted mb-3 mt-5">{t('subtitle_two')}</p>

                            <Form.Group className="mb-3" controlId="brand">
                                <FloatingLabel
                                    className="mb-3"
                                    controlId="floatingInputGrid"
                                    label={t('form.brand.placeholder')}
                                >
                                    <Form.Control
                                        type="text"
                                        placeholder={t('form.brand.placeholder')}
                                        className="text-capitalize"
                                        required
                                        aria-describedby="brandHelpBlock"
                                        autoComplete="off"
                                        value={brand}
                                        onChange={(e) => {
                                            setFormData((prevData) => ({
                                                ...prevData,
                                                brand: e.target.value,
                                            }));
                                        }}
                                    />
                                </FloatingLabel>
                            </Form.Group>
                            <Form.Text className="d-none" id="brandHelpBlock">
                                {t('accessibility.brand')}
                            </Form.Text>

                            <Form.Group className="mb-3" controlId="model">
                                <FloatingLabel
                                    className="mb-3"
                                    controlId="floatingInputGrid"
                                    label={t('form.model.placeholder')}
                                >
                                    <Form.Control
                                        type="text"
                                        placeholder={t('form.model.placeholder')}
                                        className="text-capitalize"
                                        required
                                        autoComplete="off"
                                        aria-describedby="modelHelpBlock"
                                        value={model}
                                        onChange={(e) => {
                                            setFormData((prevData) => ({
                                                ...prevData,
                                                model: e.target.value,
                                            }));
                                        }}
                                    />
                                </FloatingLabel>
                            </Form.Group>
                            <Form.Text className="d-none" id="modelHelpBlock">
                                {t('accessibility.model')}
                            </Form.Text>

                            <Form.Check
                                type="switch"
                                id="custom-switch"
                                label={t('form.isNew')}
                                onChange={(e) => {
                                    setFormData((prevData) => ({
                                        ...prevData,
                                        isNew: e.target.checked,
                                    }));
                                }}
                                checked={formData.isNew}
                            />

                            <Form.Check
                                type="switch"
                                id="custom-switch"
                                label={t('form.isNational')}
                                onChange={(e) => {
                                    setFormData((prevData) => ({
                                        ...prevData,
                                        isNational: e.target.checked,
                                    }));
                                }}
                                checked={formData.isNational}
                            />

                            <p className="text-muted mb-3 mt-5">{t('subtitle_three')}</p>

                            <Form.Group controlId="formFileMultiple" className="mb-3">
                                <Form.Label>{t('form.image.label')}</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept=".jpg, .jpeg, .png, .jfif, .webp"
                                    multiple
                                    onChange={handleFileChange}
                                    aria-describedby="imagesHelpBlock"
                                />
                            </Form.Group>
                            <Form.Text className="d-none" id="imagesHelpBlock">
                                {t('accessibility.images')}
                            </Form.Text>
                            <Row className="mb-5">
                                <Col>
                                    {imagePreviews.map((preview, index) => (
                                        <Image
                                            key={index}
                                            src={preview}
                                            alt={`Preview ${index}`}
                                            style={{ maxWidth: 100, maxHeight: 100 }}
                                            thumbnail
                                        />
                                    ))}
                                </Col>
                            </Row>
                            <ButtonToolbar
                                className="justify-content-between"
                                aria-label="Toolbar with Button groups"
                            >
                                <Button
                                    variant="secondary"
                                    type={"button"}
                                    onClick={() => navigate("/adm/productList")}
                                >
                                    {t('form.buttonCancel')}
                                </Button>
                                <Button variant="primary" type={"submit"}>
                                    {!isProccessing ? (
                                        t('updateConfirmButton')
                                    ) : (
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                    )}
                                </Button>
                            </ButtonToolbar>
                        </fieldset>
                    </Form>
                </Card.Body>
            </Card>
            <ToastContainer position="top-end" className="p-3" style={{ zIndex: 1 }}>
                <Toast
                    bg={"danger"}
                    onClose={() => setToasting(null)}
                    show={toasting !== null ? true : false}
                    delay={5000}
                    autohide
                >
                    <Toast.Header>
                        <strong className="me-auto">Montacar</strong>
                    </Toast.Header>
                    <Toast.Body className={"text-white"}>
                        {toasting !== null ? toasting : null}
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};
